import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Paper,
	Typography,
	Button,
	TextField,
	Breadcrumbs,
	Checkbox,
	CircularProgress,
	LinearProgress,
	Tooltip,
} from '@mui/material';
import {
	PlaylistPlay,
	SummarizeOutlined,
	Delete as DeleteIcon,
} from '@mui/icons-material';
import { DraggableList } from '@buildwitt/shapeshift-common/v2/quiz';
import { AdminPlaylistContentPreviewItem } from '@/app2/pages/ManagerView/Pages/Playlists/AdminPlaylistContentPreviewItem';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
	DataGridPro,
	GridColDef,
	GridRowParams,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarExport,
	GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid-pro';
import { useSSTranslation } from '@/i18n/useSSTranslation';
import {
	useGetPlaylist,
	useCreatePlaylist,
	useUpdatePlaylist,
	useDeletePlaylist,
	type CreatePlaylistBody,
	type UpdatePlaylistBody,
} from '@/app2/shared/data';
import { useCoursesListGrid } from '@/app2/pages/ManagerView/Pages/Courses/AdminCoursesList/hooks';
import type { CourseResponse } from '@/app2/shared/data/gen';
import useCustomSnackbar from '@/snackbars/useCustomSnackbar';
import ConfirmationDialog from '@/components/common/dialogs/ConfirmationDialog';

const MAX_COURSES = 25;

/** Aligned with `groups.title` VarChar(255) and playlist create/update DTOs. */
const PLAYLIST_TITLE_MAX_LENGTH = 255;

const courseColumns: GridColDef<CourseResponse>[] = [
	{
		...GRID_CHECKBOX_SELECTION_COL_DEF,
		type: 'string',
		hideable: false,
		renderCell: (params) => {
			if ((params.row.moduleCount ?? 0) === 0) {
				return (
					<Tooltip title="Cannot add courses with no lessons to a playlist">
						<span>
							<Checkbox disabled />
						</span>
					</Tooltip>
				);
			}
			return GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params) ?? null;
		},
	},
	{
		field: 'title',
		type: 'string',
		flex: 1,
		headerName: 'Course',
		minWidth: 240,
	},
	{
		field: 'moduleCount',
		type: 'number',
		flex: 1,
		headerName: 'Lessons',
		maxWidth: 110,
	},
	{
		field: 'locale',
		type: 'string',
		flex: 1,
		headerName: 'Language',
		maxWidth: 110,
		valueGetter: (params) => params.value ?? params.row?.locale ?? '—',
	},
	{
		field: 'contentType',
		type: 'string',
		flex: 1,
		headerName: 'Content Type',
	},
];

const useStyles = makeStyles()((theme) => ({
	container: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
	},
	headerContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
	},
	breadcrumbs: {
		'& svg': {
			marginRight: theme.spacing(1),
			top: '2px',
			position: 'relative',
			fontSize: '18px',
		},
	},
	title: {
		fontSize: '34px',
		fontWeight: 600,
	},
	buttonContainer: {
		display: 'flex',
		gap: theme.spacing(2),
	},
	contentContainer: {
		display: 'flex',
		gap: theme.spacing(3),
	},
	leftPaper: {
		flexGrow: 2,
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
	},
	contentPreviewPaper: {
		padding: theme.spacing(3),
		width: 300,
		minWidth: 300,
	},
	requiredLabel: {
		color: theme.palette.common.white,
		marginBottom: theme.spacing(0.5),
		display: 'block',
	},
	requiredStar: {
		color: theme.palette.error.main,
	},
	coursesGridWrapper: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
	},
	coursesGrid: {
		border: 'none',
		'& .MuiDataGrid-columnHeaders': {
			fontSize: '11px',
			fontWeight: 700,
		},
	},
}));

function CoursesGridToolbar() {
	return (
		<GridToolbarContainer>
			<GridToolbarColumnsButton
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
			<GridToolbarFilterButton {...({} as any)} />
			<GridToolbarExport
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
		</GridToolbarContainer>
	);
}

interface SelectedCourse {
	id: number;
	title: string;
}

interface AdminPlaylistPageContentProps {
	playlistId?: number;
	onSaveSuccess?: (id: number) => void;
	onDeleteSuccess?: () => void;
}

export const AdminPlaylistPageContent: FC<AdminPlaylistPageContentProps> = ({
	playlistId,
	onSaveSuccess,
	onDeleteSuccess,
}) => {
	const { classes } = useStyles();
	const { t } = useSSTranslation();
	const navigate = useNavigate();
	const { successSnackbar, errorSnackbar } = useCustomSnackbar();

	const [title, setTitle] = useState('');
	const [content, setContent] = useState('');
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [selectedCourses, setSelectedCourses] = useState<SelectedCourse[]>([]);

	const { data: playlist, isLoading: isLoadingPlaylist } = useGetPlaylist(playlistId);
	const {
		courses,
		rowCount: coursesTotalCount,
		isLoading: isLoadingCourses,
		page: coursesPage,
		pageSize: coursesPageSize,
		sortModel,
		onPageChange: onCoursesPageChange,
		onPageSizeChange: onCoursesPageSizeChange,
		onFilterChange: onCoursesFilterChange,
		onSortModelChange: onCoursesSortModelChange,
	} = useCoursesListGrid({
		initialFilterSettings: {
			page: 1,
			pageSize: 25,
			extendedAccess: true,
			sortBy: 'updatedAt',
			sortDirection: 'desc',
		},
	});
	const createMutation = useCreatePlaylist();
	const updateMutation = useUpdatePlaylist(playlistId || 0);
	const deleteMutation = useDeletePlaylist();

	const isCreating = !playlistId;
	const isSaving = createMutation.isPending || updateMutation.isPending;

	// Populate form and selected courses when playlist loads
	useEffect(() => {
		if (playlist) {
			setTitle(playlist.title);
			setContent(playlist.content || '');
			setSelectedCourses(
				(playlist.curriculum ?? []).map((c) => ({ id: c.id, title: c.title })),
			);
		}
	}, [playlist]);

	const handleSelectionChange = useCallback(
		(selectionModel: (string | number)[]) => {
			const newIds = selectionModel as number[];
			if (newIds.length > MAX_COURSES) {
				errorSnackbar(
					t('playlist_max_courses', 'You can only select up to 25 courses'),
				);
				return;
			}
			setSelectedCourses((prev) => {
				const prevIds = prev.map((c) => c.id);
				const added = newIds.filter((id) => !prevIds.includes(id));
				const removed = prevIds.filter((id) => !newIds.includes(id));
				let next = prev.filter((c) => !removed.includes(c.id));
				for (const id of added) {
					const row = courses.find((r) => r.id === id);
					next = [...next, { id, title: row?.title ?? `Course ${id}` }];
				}
				next = newIds
					.map((id) => next.find((c) => c.id === id))
					.filter((c): c is SelectedCourse => !!c);
				return next;
			});
		},
		[courses, errorSnackbar, t],
	);

	const handleRemoveFromPreview = useCallback((id: number) => {
		setSelectedCourses((prev) => prev.filter((c) => c.id !== id));
	}, []);

	const draggableListValue = useMemo(
		() =>
			selectedCourses.map((c) => ({
				id: String(c.id),
				value: c.title,
			})),
		[selectedCourses],
	);

	const handlePreviewReorder = useCallback((values: string[]) => {
		const titles = values as string[];
		setSelectedCourses((prev) => {
			if (titles.length !== prev.length) return prev;
			const reordered = titles
				.map((title) => prev.find((c) => c.title === title))
				.filter((c): c is SelectedCourse => !!c);
			if (reordered.length !== prev.length) return prev;
			if (
				reordered.map((c) => c.id).join(',') ===
				prev.map((c) => c.id).join(',')
			) {
				return prev;
			}
			return reordered;
		});
	}, []);

	const handleSave = async () => {
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			errorSnackbar(t('playlist_title_required', 'Playlist title is required'));
			return;
		}
		if (trimmedTitle.length > PLAYLIST_TITLE_MAX_LENGTH) {
			errorSnackbar(
				t('admin_course_validation_field_max_length', '{{field}} cannot exceed {{max}} characters.', {
					field: t('playlist_title', 'Title'),
					max: PLAYLIST_TITLE_MAX_LENGTH,
				}),
			);
			return;
		}

		const courseIds = selectedCourses.map((c) => c.id);

		try {
			if (isCreating) {
				const body: CreatePlaylistBody = {
					title: trimmedTitle,
					content: content.trim() || undefined,
					courseIds: courseIds.length > 0 ? courseIds : undefined,
				};
				const result = await createMutation.mutateAsync(body);
				successSnackbar(t('playlist_created', 'Playlist created successfully'));
				onSaveSuccess?.(result.id);
			} else {
				const body: UpdatePlaylistBody = {
					title: trimmedTitle,
					content: content.trim() || undefined,
					courseIds,
				};
				await updateMutation.mutateAsync(body);
				successSnackbar(t('playlist_updated', 'Playlist updated successfully'));
				onSaveSuccess?.(playlistId!);
			}
		} catch (error) {
			errorSnackbar(t('playlist_save_error', 'Failed to save playlist'));
		}
	};

	const handleDelete = async () => {
		if (!playlistId) return;

		try {
			await deleteMutation.mutateAsync(playlistId);
			successSnackbar(t('playlist_deleted', 'Playlist deleted successfully'));
			setDeleteDialogOpen(false);
			onDeleteSuccess?.();
		} catch (error) {
			errorSnackbar(t('playlist_delete_error', 'Failed to delete playlist'));
		}
	};

	const handleCancel = () => {
		navigate('/admin/playlists');
	};

	if (isLoadingPlaylist && !isCreating) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box className={classes.container}>
			<Box className={classes.header}>
				<Box className={classes.headerContent}>
					<Breadcrumbs className={classes.breadcrumbs}>
						<Link to="/admin/playlists">
							<Typography variant="body1">
								<PlaylistPlay /> {t('playlists', 'Playlists')}
							</Typography>
						</Link>
						<Typography>
							<SummarizeOutlined />
							{isCreating ? t('create', 'Create') : playlist?.title}
						</Typography>
					</Breadcrumbs>
					<Typography className={classes.title} variant="h1">
						{isCreating ? t('create_playlist', 'Create Playlist') : playlist?.title}
					</Typography>
				</Box>
				<Box className={classes.buttonContainer}>
					<Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
						{t('common_notes_cancel', 'Cancel')}
					</Button>
					<Button variant="contained" onClick={handleSave} disabled={isSaving}>
						{isSaving ? <CircularProgress size={24} /> : t('page_settings_save_button', 'Save')}
					</Button>
				</Box>
			</Box>

			<Box className={classes.contentContainer}>
				<Paper className={classes.leftPaper}>
					<Typography component="label" className={classes.requiredLabel}>
						{t('playlist_title', 'Title')}{' '}
						<Box component="span" className={classes.requiredStar}>
							*
						</Box>
					</Typography>
					<TextField
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						fullWidth
						variant="outlined"
						hiddenLabel
						inputProps={{
							maxLength: PLAYLIST_TITLE_MAX_LENGTH,
							'data-testid': 'playlist-admin-title-input',
						}}
						helperText={t('admin_course_char_count', '{{current}} / {{max}} characters', {
							current: title.length,
							max: PLAYLIST_TITLE_MAX_LENGTH,
						})}
						FormHelperTextProps={{sx: {mt: 0.5}}}
					/>
					<Box>
						<Typography component="label" className={classes.requiredLabel}>
							{t('playlist_courses', 'Courses')}{' '}
							<Box component="span" className={classes.requiredStar}>
								*
							</Box>
						</Typography>
						<Box className={classes.coursesGridWrapper}>
							<DataGridPro
								className={classes.coursesGrid}
								rows={courses}
								rowCount={coursesTotalCount}
								loading={isLoadingCourses}
								pagination
								paginationMode="server"
								page={coursesPage}
								pageSize={coursesPageSize}
								onPageChange={onCoursesPageChange}
								onPageSizeChange={onCoursesPageSizeChange}
								sortingMode="server"
								sortModel={sortModel}
								onSortModelChange={onCoursesSortModelChange}
								filterMode="server"
								onFilterModelChange={onCoursesFilterChange}
								rowsPerPageOptions={[10, 25, 50, 100, 200]}
								checkboxSelection
								keepNonExistentRowsSelected
								selectionModel={selectedCourses.map((c) => c.id)}
								onSelectionModelChange={handleSelectionChange}
								isRowSelectable={(params: GridRowParams<CourseResponse>) =>
									(params.row.moduleCount ?? 0) > 0
								}
								disableSelectionOnClick
								getRowId={(row) => row.id}
								autoHeight
								columns={courseColumns}
								localeText={{
									footerTotalRows: '',
									footerTotalVisibleRows: () => '',
									MuiTablePagination: {
										labelRowsPerPage: 'Rows per page:',
										labelDisplayedRows: ({ from, to, count }) =>
											`${from}-${to} of ${count !== -1 ? count : `${to} of more than ${to}`}`,
									},
								}}
								components={{
									Toolbar: CoursesGridToolbar,
									LoadingOverlay: LinearProgress,
								}}
							/>
						</Box>
					</Box>
				</Paper>
				<Paper className={classes.contentPreviewPaper}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						{t('playlist_content_preview', 'Content Preview')}
					</Typography>
					{selectedCourses.length === 0 && !isLoadingCourses && (
						<Typography variant="body1" color="text.secondary">
							{t(
								'playlist_select_courses_hint',
								'Select courses to build your playlist',
							)}
						</Typography>
					)}
					{isLoadingCourses && selectedCourses.length > 0 && (
						<LinearProgress sx={{ mb: 1 }} />
					)}
					{!isLoadingCourses && selectedCourses.length > 0 && (
						<DraggableList
							key={selectedCourses.map((c) => c.id).sort().join(',')}
							list={draggableListValue}
							onChange={handlePreviewReorder}
							componentOverrides={{
								draggableListItem: (e) => (
									<AdminPlaylistContentPreviewItem
										{...e}
										key={e.item.id}
										onDelete={() =>
											handleRemoveFromPreview(Number(e.item.id))
										}
									/>
								),
							}}
						/>
					)}
				</Paper>
			</Box>

			{!isCreating && (
				<>
					<Button
						variant="outlined"
						color="error"
						startIcon={<DeleteIcon />}
						onClick={() => setDeleteDialogOpen(true)}
						sx={{ width: '100px' }}
					>
						{t('common_notes_delete', 'DELETE')}
					</Button>

					<ConfirmationDialog
						title={t('delete_playlist_title', 'Delete Playlist')}
						content={t(
							'delete_playlist_message',
							'This playlist will be permanently deleted. Users will no longer see these courses in their Training Feed, but all their progress will be saved.'
						)}
						open={deleteDialogOpen}
						onCancel={() => setDeleteDialogOpen(false)}
						onConfirm={handleDelete}
						testIdPrefix="deletePlaylist"
					/>
				</>
			)}
		</Box>
	);
};

export default AdminPlaylistPageContent;
